import { LEAGUES, resolveSupportedLeagueCode } from './leagues';

/** Historical leagues removed from playable `LEAGUES` but still present on stored achievements. */
const LEGACY_ACHIEVEMENT_LEAGUE_NAMES = {
  BSA: 'Campeonato Brasileiro Série A',
  WC: 'FIFA World Cup'
};

const LEGACY_ACHIEVEMENT_LEAGUE_CODES = Object.keys(LEGACY_ACHIEVEMENT_LEAGUE_NAMES).sort(
  (a, b) => b.length - a.length
);

/**
 * Resolve a league code for achievement labeling (active LEAGUES + legacy BSA/WC).
 * @param {string} value
 * @returns {string|null}
 */
function resolveAchievementLeagueCode(value) {
  if (!value) return null;
  const supported = resolveSupportedLeagueCode(value);
  if (supported) return supported;
  const normalized = String(value).toUpperCase();
  return LEGACY_ACHIEVEMENT_LEAGUE_CODES.find((code) => normalized.startsWith(code)) || null;
}

function competitionName(apiCode) {
  return (
    LEAGUES.find((l) => l.code === apiCode)?.name ??
    LEGACY_ACHIEVEMENT_LEAGUE_NAMES[apiCode] ??
    apiCode
  );
}

function parseSeasonLeagueCode(code) {
  const apiCode = resolveAchievementLeagueCode(code);
  if (!apiCode) return null;
  const tail = code.slice(apiCode.length);
  if (!/^\d{4}$/.test(tail)) return null;
  return { apiCode, competitionName: competitionName(apiCode), year: tail };
}

function parseRoundLeagueCode(code) {
  const apiCode = resolveAchievementLeagueCode(code);
  if (!apiCode) return null;
  const tail = code.slice(apiCode.length);
  const m = tail.match(/^(\d{4})(\d{2})$/);
  if (!m) return null;
  const roundNumber = parseInt(m[2], 10);
  if (!Number.isFinite(roundNumber)) return null;
  return { apiCode, competitionName: competitionName(apiCode), year: m[1], roundNumber };
}

/** Round number from a round league code (e.g. PL202538 → 38). */
export function roundNumberFromRoundCode(roundCode) {
  const raw = String(roundCode ?? '').trim();
  if (!raw) return null;
  const parsed = parseRoundLeagueCode(raw.startsWith('@') ? raw.slice(1) : raw);
  return parsed?.roundNumber ?? null;
}

export function isCoinAchievementLeague(leagueCode) {
  return String(leagueCode ?? '').trim().startsWith('@');
}

/**
 * Human-readable achievement league label from standings `lc` codes.
 * Coin leagues (@roundCode) use the same label as the round; pair with {@link isCoinAchievementLeague} + coin icon.
 */
export function formatAchievementLeagueLabel(leagueCode) {
  const raw = String(leagueCode ?? '').trim();
  if (!raw) return '—';

  if (raw.startsWith('@')) {
    const inner = parseRoundLeagueCode(raw.slice(1));
    if (inner) {
      return `${inner.competitionName} · Round ${inner.roundNumber}`;
    }
    return raw;
  }

  const hashIdx = raw.indexOf('#');
  if (hashIdx > 0) {
    const seasonPart = raw.slice(0, hashIdx);
    const rest = raw.slice(hashIdx + 1);
    const lastHash = rest.lastIndexOf('#');
    if (lastHash > 0) {
      const cell = rest.slice(0, lastHash);
      const tierDiv = cell.match(/^(\d+)-(\d+)$/);
      const season = parseSeasonLeagueCode(seasonPart);
      if (tierDiv && season) {
        return `${season.competitionName} · Tier ${tierDiv[1]}, Div ${tierDiv[2]}`;
      }
    }
  }

  const round = parseRoundLeagueCode(raw);
  if (round) {
    return `${round.competitionName} · Round ${round.roundNumber}`;
  }

  const season = parseSeasonLeagueCode(raw);
  if (season) {
    return `${season.competitionName} · Season`;
  }

  return raw;
}
